import React, { useEffect } from 'react';
import { cn } from 'src/lib/utils';
import { Link } from 'src/components/ui/link';
import { RequestPageHeading } from 'src/features/requests/request-page-heading';
import { appRoutes } from 'src/routes';
import { generatePath, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useRequestId } from 'src/features/requests/use-request-id';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { DecoratedRequestProvider } from 'src/features/requests/use-decorated-request-context';
import { ErrorBoundary } from 'react-error-boundary';
import { RequestQueryProvider } from 'src/features/requests/use-request-query-context';
import { CompanyQueryProvider } from 'src/features/companies/use-company-query-context';
import { useCompanyId } from 'src/features/companies/use-company-id';
import { useCurrentCompanyRequestsCounts } from 'src/models';
import {
  RequestDefaultPageMainContent,
  RequestDefaultPageSkeleton,
  RequestPageContainer,
  RequestPageContentMain,
} from 'src/pages/request/request-page/request-default-page';
import {
  RequestMultiOutcomePageMainContent,
  RequestMultiOutcomeSkeleton,
} from 'src/pages/request/request-page/request-multi-outcome-page';
import { RequestResponse } from 'src/lib/services/api/request-api';
import { RequestMultiOutcomePageContentTop } from 'src/pages/request/request-page/request-multi-outcome-page-content-top';
import {
  RequestOutcomePageContentTop,
  RequestOutcomePageMainContent,
} from 'src/pages/request/request-page/request-outcome-page';
import { RequestDefaultPageContentTop } from 'src/pages/request/request-page/request-default-page-content-top';
import { SpaceErrorFallback } from 'src/features/fallback';
import {
  NotFoundFallback,
  NotFoundFallbackAction,
  NotFoundFallbackDescription,
  NotFoundFallbackHeader,
  NotFoundFallbackImage,
  NotFoundFallbackTitle,
} from 'src/features/fallback/ui';
import { HttpError } from 'src/api/Http/HttpError';
import { ProjectFilesUrlModalProvider } from 'src/features/requests/project-files-url-modal';

const RequestNotFoundFallback: React.FC = () => {
  const { t } = useTranslation();
  const companyId = useCompanyId();
  const navigate = useNavigate();

  return (
    <NotFoundFallback className={'tw-mx-auto tw-max-w-[32rem] tw-items-center tw-text-center'}>
      <NotFoundFallbackImage className={'tw-mx-auto tw-max-w-xs'} />
      <NotFoundFallbackHeader>
        <NotFoundFallbackTitle>
          {t('requests:not_found.title', {
            defaultValue: 'This request is no longer available',
          })}
        </NotFoundFallbackTitle>
        <NotFoundFallbackDescription>
          {t('requests:not_found.description', {
            defaultValue: 'It may have been deleted or archived.',
          })}
        </NotFoundFallbackDescription>
      </NotFoundFallbackHeader>
      <NotFoundFallbackAction
        onClick={() => navigate(generatePath(appRoutes.companyRequestsList, { companyId }))}
        className={'tw-min-w-36'}
        variant={'outline'}
      >
        {t('actions:back_to_requests', { defaultValue: 'Back to requests' })}
      </NotFoundFallbackAction>
    </NotFoundFallback>
  );
};

const RequestPageFallbackContent: React.FC<{ error: any; resetErrorBoundary: () => any }> = ({
  error,
  resetErrorBoundary,
}) => {
  const { t } = useTranslation();
  const companyId = useCompanyId();
  const requestId = useRequestId();

  // A request that's genuinely gone (deleted/never existed) can't be fixed by
  // retrying, unlike the transient errors SpaceErrorFallback is meant for.
  // requestClient (Api.http) reports the status as `.code`, not `.status`.
  const isNotFound = error instanceof HttpError && error.code === 404;

  return (
    <>
      {/* A distinct string, not requests:not_found.title — that key is already the in-body
          fallback's longer sentence, and reusing it here would give one key two defaults. */}
      <RequestPageHeading
        title={isNotFound ? t('Request not found') : t('Request')}
        companyId={companyId}
      />

      <div className={'tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-12'}>
        {!isNotFound && (
          <Button
            asChild
            size={'sm'}
            variant={'default'}
            className={'tw-mx-auto tw-grow sm:tw-me-0 sm:tw-ms-auto sm:tw-grow-0'}
          >
            <Link
              to={generatePath(appRoutes.requestRevisions, {
                companyId,
                requestId,
              })}
              variant={'unset'}
            >
              {t('models/revision:name.plural')}
            </Link>
          </Button>
        )}
      </div>

      {isNotFound ? (
        <RequestNotFoundFallback />
      ) : (
        <SpaceErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
      )}
    </>
  );
};

const RequestPageSkeletonResolver: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    flow?: RequestResponse['flow'];
  }
> = ({ flow = 'default', className, ...props }) => {
  const { t } = useTranslation();
  const companyId = useCompanyId();

  const skeleton = (() => {
    switch (flow) {
      case 'default':
        return (
          <RequestDefaultPageSkeleton
            className={cn('tw-col-span-12 tw-gap-4', className)}
            {...props}
          />
        );
      case 'multi_outcome':
        return (
          <RequestMultiOutcomeSkeleton
            {...props}
            className={cn('tw-col-span-12 tw-gap-4', className)}
          />
        );
      case 'repurpose':
        return (
          <RequestMultiOutcomeSkeleton
            {...props}
            className={cn('tw-col-span-12 tw-gap-4', className)}
          />
        );
      case 'outcome':
        return (
          <RequestDefaultPageSkeleton
            className={cn('tw-col-span-12 tw-gap-4', className)}
            {...props}
          />
        );
      default:
        return (
          <RequestDefaultPageSkeleton
            className={cn('tw-col-span-12 tw-gap-4', className)}
            {...props}
          />
        );
    }
  })();

  return (
    <>
      {/* The loaded content-tops own the heading, but they are not mounted while the request is
          in flight — without this the sticky header is blank and the document has no h1 for the
          whole fetch (#2611). */}
      <RequestPageHeading title={t('Loading request')} companyId={companyId} />
      {skeleton}
    </>
  );
};

const RequestFlowMainContentResolver: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    flow?: RequestResponse['flow'];
  }
> = ({ flow = 'default', className, ...props }) => {
  switch (flow) {
    case 'multi_outcome':
      return (
        <RequestMultiOutcomePageMainContent
          className={cn('tw-col-span-12', className)}
          {...props}
        />
      );
    case 'repurpose':
      return (
        <RequestMultiOutcomePageMainContent
          className={cn('tw-col-span-12', className)}
          {...props}
        />
      );
    case 'outcome':
      return (
        <RequestOutcomePageMainContent className={cn('tw-col-span-12', className)} {...props} />
      );
    default:
      return (
        <RequestDefaultPageMainContent className={cn('tw-col-span-12', className)} {...props} />
      );
  }
};
RequestFlowMainContentResolver.displayName = 'RequestFlowContentResolver';

const RequestFlowContentTopResolver: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    flow?: RequestResponse['flow'];
  }
> = ({ flow = 'default', ...props }) => {
  switch (flow) {
    case 'default':
      return <RequestDefaultPageContentTop {...props} />;
    case 'multi_outcome':
      return <RequestMultiOutcomePageContentTop {...props} />;
    case 'repurpose':
      return <RequestMultiOutcomePageContentTop {...props} />;
    case 'outcome':
      return <RequestOutcomePageContentTop {...props} />;
    default:
      return <div {...props}>Unknown request flow type content top</div>;
  }
};

const RequestPage: React.FC = () => {
  const companyId = useCompanyId();
  const requestId = useRequestId();

  const { review, query: companyRequestsCountsQuery } = useCurrentCompanyRequestsCounts();

  useEffect(() => {
    if (companyRequestsCountsQuery.isSuccess) {
      review(requestId!);
    }
  }, [companyRequestsCountsQuery.isSuccess]);

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          fallbackRender={({ error, resetErrorBoundary }: any) => (
            <RequestPageFallbackContent error={error} resetErrorBoundary={resetErrorBoundary} />
          )}
          onReset={reset}
        >
          <React.Suspense fallback={<RequestPageSkeletonResolver flow={'default'} />}>
            <CompanyQueryProvider id={companyId}>
              <RequestQueryProvider id={requestId}>
                {({ data }) => {
                  // reviewed_by_editor is auto-updated by backend when fetching request
                  // No manual marking needed

                  return (
                    <ProjectFilesUrlModalProvider>
                      <DecoratedRequestProvider request={data}>
                        <RequestPageContainer>
                          <RequestFlowContentTopResolver flow={data?.flow} />

                          <RequestPageContentMain className={'tw-grid-cols-1 md:tw-grid-cols-12'}>
                            <RequestFlowMainContentResolver flow={data?.flow} />
                          </RequestPageContentMain>
                        </RequestPageContainer>
                      </DecoratedRequestProvider>
                    </ProjectFilesUrlModalProvider>
                  );
                }}
              </RequestQueryProvider>
            </CompanyQueryProvider>
          </React.Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
};

export { RequestPageContainer, RequestPage };
